import type { LookalikePersona, LookalikeView } from '@repo/contracts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeadLabel,
  TableRow,
  TableWrap,
} from '@repo/ui/table';

import type { LookalikeFilterField } from '../store/persona-lookalikes-store';
import { THIN_SCROLLBAR } from './lookalike-scrollbar';

const ROW_LIMIT = 30;

function matches(
  lookalike: LookalikePersona,
  field: LookalikeFilterField | null,
  value: string | null,
): boolean {
  if (!field || !value) return true;
  return lookalike[field] === value;
}

/**
 * The accessible fallback for the scatter — same filtered set, as real rows a
 * screen reader can announce column-by-column. Capped at 30: a table of 1000
 * rows is not more legible than a chart of 1000 points, and this exists for
 * "let me actually read a few of these," not for browsing the whole
 * population.
 */
export function LookalikesTable({
  filterField,
  filterValue,
  view,
}: {
  filterField: LookalikeFilterField | null;
  filterValue: string | null;
  view: LookalikeView;
}) {
  const filtered = view.lookalikes.filter((l) =>
    matches(l, filterField, filterValue),
  );
  const rows = filtered.slice(0, ROW_LIMIT);

  return (
    <div>
      <p className="mb-2 font-mono text-caption text-text-muted">
        Showing {rows.length.toLocaleString()} of{' '}
        {filtered.length.toLocaleString()}
        {filtered.length === view.lookalikes.length ? '' : ' filtered'}{' '}
        lookalikes
      </p>
      <TableWrap className={`border-hairline shadow-soft ${THIN_SCROLLBAR}`}>
        <Table>
          <TableHeader>
            <TableRow className="border-hairline">
              <TableHead className="border-hairline">
                <TableHeadLabel>Job title</TableHeadLabel>
              </TableHead>
              <TableHead className="border-hairline">
                <TableHeadLabel>Company size</TableHeadLabel>
              </TableHead>
              <TableHead className="border-hairline">
                <TableHeadLabel>Location</TableHeadLabel>
              </TableHead>
              <TableHead className="border-hairline">
                <TableHeadLabel>Experience</TableHeadLabel>
              </TableHead>
              <TableHead className="border-hairline">
                <TableHeadLabel>Pain point</TableHeadLabel>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((lookalike) => (
              <TableRow key={lookalike.id}>
                <TableCell>{lookalike.jobTitle}</TableCell>
                <TableCell>{lookalike.companySize}</TableCell>
                <TableCell>{lookalike.location}</TableCell>
                <TableCell className="font-mono tabular-nums">
                  {lookalike.experienceYears} yrs
                </TableCell>
                <TableCell>{lookalike.painPoint}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableWrap>
    </div>
  );
}
